import { DiffTable, changesRows } from "@/ui/DiffTable";
import { FilterRow, ActiveFilters } from "@/ui/FilterRow";
import { Fragment, useEffect, useState } from "react";
import { useRead, useWrite } from "@/api/useTool";
import * as api from "@/api/tools";
import { businessLocalToISO } from "@/api/runtime";
import { MISSING, CopyId, Identity, SectionNav, Banner, Dialog, Empty, Field, KV, Pager, Pill, ReasonDialog, Receipt, RecordFields, Skeleton, Table, downloadText, recordLabel, roleLabel, fmtDate } from "@/ui";
import { useSession } from "@/session/useSession";
import { useViewState } from "@/shell/viewState";
import { go } from "@/router";
import { AdminConfig } from "./AdminConfig";
import { AdminArchive } from "./AdminArchive";
import { AdminPeople, AdminDelegation, allPeople } from "./AdminPeople";

const TABS = [
  ["feedback", "Feedback", "feedback.list"],
  ["outbox", "Outbox", "admin.outbox_list"],
  ["audit", "Audit", "audit.query"],
  ["config", "Config", "config.list"],
  ["access", "Access", "admin.people_list"],
  ["delegation", "Delegation", "delegation.list"],
  ["archive", "Archive", "admin.archive_search"],
  ["system", "System", "admin.system"],
] as const;
export function Admin({ tab, configKey, version }: { tab?: string | undefined; configKey?: string | undefined; version?: string | undefined }) {
  const { can, me } = useSession();
  const { register } = useViewState();
  // The tabs are the manifest's (BRD §8: his TA Admin configures and his HR Systems Admin runs the system and the
  // ledger, both the one Admin since DC-228); the server refuses every call of a tab that is not listed, so no role
  // table lives here.
  const admin = !!me && (can("feedback.list") || can("config.list"));
  const tabs = admin ? TABS.filter(([, , tool]) => can(tool)) : [];
  const t = tab ?? tabs[0]?.[0] ?? "feedback";
  useEffect(() => register({ view: "admin", ids: { tab: t }, summary: { tab: t } }), [register, t]);
  return (
    <div className="admin-page">
      <h1 className="page">Admin</h1>
      <p className="lede">Configuration, access and retained evidence. Every operation has a receipt.</p>
      <SectionNav label="Admin sections" links={tabs.map(([key, label]) => ({ label, href: `#admin/${key}`, current: t === key }))} />
      {!tabs.some(([k]) => k === t) ? (
        <Empty heading="Admin access is required">This Admin screen is not available to your role.</Empty>
      ) : (
        <div key={t}>
          {t === "feedback" && <FeedbackTab />}
          {t === "outbox" && <OutboxTab />}
          {t === "audit" && <AuditTab />}
          {t === "config" && <AdminConfig configKey={configKey} version={version} />}
          {t === "access" && <AdminPeople />}
          {t === "delegation" && <AdminDelegation />}
          {t === "archive" && <AdminArchive />}
          {t === "system" && <SystemTab />}
        </div>
      )}
    </div>
  );
}
const STATUSES = { new: "New", triaged: "Triaged", in_progress: "In progress", fixed: "Fixed", wont_fix: "Won't fix", duplicate_of: "Duplicate of" };
const date = (v: string) => (v ? businessLocalToISO(v) : null);

// F-41: the admin screens in his words. A person reads by name, never "name · p_id" (an audit receipt keeps its ids on
// its technical line, under Changes). Tools, outcomes, refusals, template keys and spans go through the tables and
// formatters below, never raw.
const SYSTEM_PERSON = "p_system";
type Directory = ReadonlyMap<string, string>;
/** The roster's names by person id, read once for every admin tab (the Audit filters already read it). */
function useDirectory(): Directory {
  const { can } = useSession();
  const people = useRead(["all-people"], allPeople, { enabled: can("admin.people_list") });
  return new Map((people.data ?? []).map((p) => [p.person_id, p.name]));
}
/** A person's label: the name the row carries, else the roster's; the worker is System; an unknown id stays unshown. */
export function personName(directory: Directory, id: string | null | undefined, name?: string | null | undefined): string {
  if (name) return name;
  if (!id || id === SYSTEM_PERSON) return roleLabel("system");
  return directory.get(id) ?? MISSING;
}
const REPORTER_ROLE: Record<string, string> = { anonymous: "Not signed in" };
const AUDIT_OUTCOME: Record<string, string> = { ok: "Completed", refused: "Refused" };
/** The refusal codes of POLICY §9.1 in words; a "CODE / rule" refusal reads both halves. */
const REFUSAL: Record<string, string> = {
  NOT_ROLE: "Not your role", NOT_SEATED: "Not seated on this record", NOT_OWNER: "Not the owner", NOT_AUTHOR: "Not the author",
  GRANT_EXPIRED: "Access grant expired", DELEGATION_NOT_COVERING: "Stand-in does not cover this", SOD_CONFLICT: "Separation of duties",
  SEALED: "Sealed", RECORD_LOCKED: "Record locked", RECORD_ARCHIVED: "Record archived", RESTRICTED_CONTENT: "Restricted content",
  FIELD_NOT_OWNED: "Field not owned", VIEW_AS_READ_ONLY: "View as is read-only", VIEW_AS_EXPIRED: "View as expired",
  ACTOR_INACTIVE: "Person inactive", ENV_NOT_ALLOWED: "Not allowed in this environment", IDEMPOTENCY_KEY_REUSE: "Repeated request",
  NOT_FOUND_OR_NOT_PERMITTED: "Not found or not permitted", RULE_UNMET: "Rule not met",
};
const sentence = (key: string) => { const words = key.toLowerCase().replace(/_/g, " ").trim(); return words.charAt(0).toUpperCase() + words.slice(1); };
export const refusalWords = (reason: string): string => reason.split(" / ").map((part, i) => i === 0 ? REFUSAL[part] ?? sentence(part) : part.replace(/_/g, " ")).join(" · ");
/** A tool by its object and verb in words ("requisition.open" reads "Requisition · Open"). */
export const toolWords = (tool: string): string => tool.split(".").map(recordLabel).join(" · ");
/** The Outbox's message kinds: each template's name. */
const OUTBOX_KIND: Record<string, string> = {
  intake_part1: "Intake Part 1", kickoff_email: "Kickoff email", kickoff_correction: "Kickoff correction", interview_invite: "Interview invitation",
  debrief_invite: "Debrief invitation", offer_event: "Offer update", phase_flag: "Phase flag", resume_batch: "Résumé batch",
};
export const outboxKind = (kind: string): string => OUTBOX_KIND[kind] ?? recordLabel(kind);
/** A delivery's subject without the simulated tag (the status cell says Simulated); a subject that is only its template's key reads as the template's name. */
export function deliverySubject(row: Pick<api.OutboxRow, "kind" | "notification">): string {
  const subject = String(row.notification.subject ?? "").replace(/^\[SIMULATED\]\s*/, "").trim();
  return !subject || subject === row.kind ? outboxKind(row.kind) : subject;
}
const count = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
/** A span in hours and minutes (seconds under a minute, days past a day), with correct plurals. */
export function duration(seconds: number | null | undefined): string {
  const total = Math.max(0, Math.round(seconds ?? 0));
  if (total < 60) return count(total, "second");
  const minutes = Math.floor(total / 60);
  if (minutes < 60) return count(minutes, "minute");
  const hours = Math.floor(minutes / 60), restMinutes = minutes % 60;
  if (hours < 24) return restMinutes ? `${count(hours, "hour")} ${count(restMinutes, "minute")}` : count(hours, "hour");
  const days = Math.floor(hours / 24), restHours = hours % 24;
  return restHours ? `${count(days, "day")} ${count(restHours, "hour")}` : count(days, "day");
}
function Filters({
  values,
  labels,
  update,
  dates = [],
  choices = {},
  suggestions = {},
}: {
  values: Record<string, string>;
  labels?: Record<string, string>;
  update: (k: string, v: string) => void;
  dates?: string[];
  choices?: Record<string, string[]>;
  suggestions?: Record<string, string[]>;
}) {
  return (
    <FilterRow>
      {Object.entries(values).map(([k, v]) => (
        <Field key={k} label={labels?.[k] ?? k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())} id={`filter-${k}`}>
          {choices[k] ? <select id={`filter-${k}`} value={v} onChange={(e) => update(k, e.target.value)}><option value="">Any</option>{choices[k].map((key) => <option key={key} value={key}>{key.replace(/_/g, " ")}</option>)}</select> : <input id={`filter-${k}`} type={dates.includes(k) ? "datetime-local" : "text"} list={suggestions[k] ? `filter-${k}-suggestions` : undefined} value={v} onChange={(e) => update(k, e.target.value)} />}
          {suggestions[k] && <datalist id={`filter-${k}-suggestions`}>{suggestions[k].map(value => <option key={value} value={value} />)}</datalist>}
        </Field>
      ))}
    </FilterRow>
  );
}
function FeedbackTab() {
  const directory = useDirectory();
  const [filters, setFilters] = useState({ query: "", route_prefix: "", role: "", since: "", until: "" });
  const [status, setStatus] = useState<Exclude<api.FeedbackListInput["status"], undefined>>(() => new URLSearchParams(location.hash.split("?")[1]).get("status") === "new" ? "new" : null);
  const [pages, setPages] = useState<(string | null)[]>([null]);
  const [opened, setOpened] = useState<string | null>(null);
  const input = { ...filters, since: date(filters.since), until: date(filters.until), status, limit: 25, cursor: pages.at(-1) ?? null };
  const q = useRead(["feedback", input], () => api.feedbackList(input));
  const exporter = useWrite(async (format: "json" | "csv") => {
    const data = await api.feedbackExport({ ...input, format });
    downloadText(
      `feedback.${format}`,
      format === "csv" ? (data.csv ?? "") : JSON.stringify(data.items, null, 2),
      format === "csv" ? "text/csv" : "application/json",
    );
    return data.rows;
  });
  return (
    <section>
      <h2 className="sec">Feedback</h2>
      <Filters
        values={filters}
        dates={["since", "until"]}
        labels={{ query: "Search", route_prefix: "Route prefix", since: "From", until: "To" }}
        update={(k, v) => {
          setFilters({ ...filters, [k]: v });
          setPages([null]);
        }}
      />
      <Field label="Status" id="feedback-status">
        <select
          id="feedback-status"
          value={status ?? ""}
          onChange={(e) => {
            setStatus((e.target.value as typeof status) || null);
            setPages([null]);
          }}
        >
          <option value="">Any status</option>
          {Object.entries(STATUSES).map(([s, l]) => (
            <option key={s} value={s}>
              {l}
            </option>
          ))}
        </select>
      </Field>
      <div className="acts">
        <button className="btn" disabled={exporter.isPending} onClick={() => exporter.mutate("csv")}>
          Export CSV
        </button>
        <button className="btn" disabled={exporter.isPending} onClick={() => exporter.mutate("json")}>
          Export JSON
        </button>
      </div>
      <Banner error={exporter.error} />
      {exporter.isSuccess && <Receipt>Exported {exporter.data} reports from the complete filtered set.</Receipt>}
      <ActiveFilters values={filters} clear={(key) => { setFilters({ ...filters, [key]: "" }); setPages([null]); }} />
      <Banner error={q.error} onRetry={() => void q.refetch()} />
      {q.isLoading && <Skeleton />}
      {q.data && (
        <>
          {!q.data.items.length ? (
            <Empty heading={status || Object.values(filters).some(Boolean) ? "No matching feedback" : "No feedback yet"} action={status || Object.values(filters).some(Boolean) ? <button className="btn" onClick={() => { setFilters({ query: "", route_prefix: "", role: "", since: "", until: "" }); setStatus(null); setPages([null]); }}>Clear filters</button> : undefined}>{status || Object.values(filters).some(Boolean) ? "Try another search or clear the filters." : "Every screen has a Bug button."}</Empty>
          ) : (
            <Table
              label="Feedback"
              head={
                <tr>
                  <th>Report / when</th>
                  <th>Reporter / route</th>
                  <th>Title</th>
                  <th>Status / owner</th>
                  <th>Updated</th>
                </tr>
              }
            >
              {q.data.items.map((r) => (
                <tr key={r.id}>
                  <td>
                    <button className="link" onClick={() => setOpened(r.id)}>
                      {r.public_ref}
                    </button>
                    <div>{fmtDate(r.created_at, true)}</div>
                  </td>
                  <td>
                    {personName(directory, r.reporter_id)} · {REPORTER_ROLE[r.role] ?? roleLabel(r.role)}
                    <div className="long">{r.route}</div>
                  </td>
                  <td className="long">{r.note.split("\n")[0]}</td>
                  <td>
                    <Pill tone={r.status === "new" ? "warn" : "info"}>{STATUSES[r.status as keyof typeof STATUSES] ?? r.status}</Pill>
                    <div>{r.owner_id ? personName(directory, r.owner_id) : "Unassigned"}</div>
                  </td>
                  <td>{fmtDate(r.updated_at, true)}</td>
                </tr>
              ))}
            </Table>
          )}
          <Pager count={q.data.items.length} page={pages.length}
            previous={pages.length > 1 ? () => setPages(pages.slice(0, -1)) : undefined}
            next={q.data.next_cursor ? () => setPages([...pages, q.data!.next_cursor!]) : undefined}
          />
        </>
      )}
      {opened && <FeedbackDetail id={opened} onClose={() => setOpened(null)} />}
    </section>
  );
}
function FeedbackDetail({ id, onClose }: { id: string; onClose: () => void }) {
  const directory = useDirectory();
  const q = useRead(["feedback-open", id], () => api.feedbackOpen({ id }));
  const people = useRead(["feedback-owners"], () => api.adminPeopleList({ capability: "admin", status: "active", limit: 100 }));
  const [status, setStatus] = useState<api.FeedbackTriageInput["status"]>("new");
  const [owner, setOwner] = useState("");
  const [note, setNote] = useState("");
  const [duplicate, setDuplicate] = useState("");
  useEffect(() => {
    if (q.data) {
      setStatus(q.data.status as typeof status);
      setOwner(q.data.owner_id ?? "");
      setNote(q.data.resolution ?? "");
      setDuplicate(q.data.duplicate_of_id ?? "");
    }
  }, [q.data]);
  const save = useWrite(api.feedbackTriage, [["feedback"], ["feedback-open", id]]);
  return (
    <Dialog title={q.data ? `${q.data.public_ref} · ${q.data.note.split("\n")[0]?.slice(0, 100)}` : "Feedback"} titleAside={q.data ? <Pill tone="info">{STATUSES[q.data.status as keyof typeof STATUSES]}</Pill> : null} open onClose={onClose}>
      <Banner error={q.error} onRetry={() => void q.refetch()} />
      {q.isLoading && <Skeleton />}
      {q.data && (
        <>
          <p className="long admin-note">{q.data.note}</p>
          <details><summary>Technical details</summary><CopyId value={id} /></details>
          <KV
            items={[
              ["Reporter", personName(directory, q.data.reporter_id, typeof q.data.reporter_contact?.name === "string" ? q.data.reporter_contact.name : null)],
              ["Role", REPORTER_ROLE[q.data.role] ?? roleLabel(q.data.role ?? MISSING)],
              ["Reported", fmtDate(q.data.created_at, true)],
              ["Route", q.data.route],
            ]}
          />
          <h3 className="sec">Reporter contact</h3>
          <RecordFields value={q.data.reporter_contact} />
          <h3 className="sec">Context preview</h3>
          {Object.keys(q.data.context ?? {}).length ? <RecordFields value={q.data.context} /> : <p className="muted">No context was captured with this report.</p>}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              save.mutate({ id, status, owner_id: owner || null, resolution: note || null, duplicate_of_id: status === "duplicate_of" ? duplicate : null });
            }}
          >
            <Field label="Status" id="triage-status">
              <select id="triage-status" value={status} onChange={(e) => setStatus(e.target.value as typeof status)}>
                {Object.entries(STATUSES).map(([s, l]) => (
                  <option key={s} value={s}>
                    {l}
                  </option>
                ))}
              </select>
            </Field>
            {status === "duplicate_of" && (
              <Field label="Duplicate of" required id="duplicate">
                <input id="duplicate" required value={duplicate} onChange={(e) => setDuplicate(e.target.value)} />
              </Field>
            )}
            <Banner error={people.error} />
            <Field label="Owner" id="triage-owner">
              <select id="triage-owner" value={owner} onChange={(e) => setOwner(e.target.value)}>
                <option value="">Unassigned</option>
                {people.data?.items.map((p) => (
                  <option value={p.person_id} key={p.person_id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Internal note" id="triage-note">
              <textarea id="triage-note" value={note} onChange={(e) => setNote(e.target.value)} />
            </Field>
            <Banner error={save.error} />
            {save.isSuccess && <Receipt>Feedback updated · {STATUSES[status]}</Receipt>}
            <button className="btn btn--primary" disabled={save.isPending}>
              Save triage
            </button>
          </form>
        </>
      )}
    </Dialog>
  );
}
function OutboxTab() {
  const { me } = useSession();
  const directory = useDirectory();
  const [filters, setFilters] = useState(() => ({ state: new URLSearchParams(location.hash.split("?")[1]).get("attention") === "1" ? "Needs attention" : "", kind: "", requisition_id: "", from: "", to: "" }));
  const [pages, setPages] = useState<(string | null)[]>([null]);
  const [opened, setOpened] = useState<string | null>(null);
  const input = { ...filters, state: filters.state === "Needs attention" ? null : filters.state, attention_only: filters.state === "Needs attention", from: date(filters.from), to: date(filters.to), cursor: pages.at(-1) ?? null, limit: 25 };
  const q = useRead(["outbox", input], () => api.adminOutboxList(input));
  const [faultOpen, setFaultOpen] = useState(false);
  const [channel, setChannel] = useState<api.AdminOutboxFaultNextInput["channel"]>("mail");
  const [outcome, setOutcome] = useState<api.AdminOutboxFaultNextInput["outcome"]>("failed");
  const fault = useWrite(api.adminOutboxFaultNext);
  return (
    <section>
      <h2 className="sec">Outbox</h2>
      <p className="callout">Simulated delivery — nothing left the application.</p>
      <Filters
        values={filters}
        dates={["from", "to"]}
        labels={{ state: "Status", requisition_id: "Requisition", from: "From", to: "To" }}
        update={(k, v) => {
          setFilters({ ...filters, [k]: v });
          setPages([null]);
        }}
      />
      {me && ["dev", "test"].includes(me.env) && (
        <details className="card">
          <summary>Development controls · simulated failures</summary>
          <button className="btn" onClick={() => setFaultOpen(true)}>
            Inject a failure on next send
          </button>
          {fault.isSuccess && (
            <Receipt>
              Next {fault.data.channel} delivery: injected {fault.data.outcome}.
            </Receipt>
          )}
        </details>
      )}
      <ActiveFilters values={filters} clear={(key) => { setFilters({ ...filters, [key]: "" }); setPages([null]); }} />
      <Banner error={q.error} onRetry={() => void q.refetch()} />
      {q.isLoading && <Skeleton />}
      {q.data && (
        <>
          {!q.data.items.length ? (
            <Empty heading={Object.values(filters).some(Boolean) ? "No matching deliveries" : "No deliveries yet"} action={Object.values(filters).some(Boolean) ? <button className="btn" onClick={() => { setFilters({ state: "", kind: "", requisition_id: "", from: "", to: "" }); setPages([null]); }}>Clear filters</button> : undefined}>{Object.values(filters).some(Boolean) ? "No deliveries match these filters." : "Deliveries appear here when the application sends a message or calendar invitation."}</Empty>
          ) : (
            <Table
              label="Outbox deliveries"
              head={
                <tr>
                  <th>Message / queued</th>
                  <th>To / from</th>
                  <th>Subject / kind</th>
                  <th>Requisition</th>
                  <th>Status / attempts</th>
                </tr>
              }
            >
              {q.data.items.map((r) => (
                <tr key={r.id}>
                  <td>
                    <button className="link" onClick={() => setOpened(r.id)}>
                      {r.id}
                    </button>
                    <div>{fmtDate(r.created_at, true)}</div>
                  </td>
                  <td>
                    <RecordFields value={notificationPerson(directory, r.notification, "to")} />
                    {notificationPerson(directory, r.notification, "from") != null && <div className="muted">from <RecordFields value={notificationPerson(directory, r.notification, "from")} /></div>}
                  </td>
                  <td className="long">
                    {deliverySubject(r)}
                    {deliverySubject(r) !== outboxKind(r.kind) && <div className="muted">{outboxKind(r.kind)}</div>}
                  </td>
                  <td>{r.requisition_id ?? MISSING}</td>
                  <td>
                    <Pill tone={["failed", "dead"].includes(r.state) ? "bad" : "info"}>
                      {r.label.replace(/\s*\(simulated\)/i, "")}
                    </Pill>
                    {/simulated/i.test(r.label) && <div>Simulated</div>}
                    {r.suppressed_reason && <div>{r.suppressed_reason}</div>}
                    <div>
                      {r.attempts} {r.attempts === 1 ? "attempt" : "attempts"}{r.handled_at ? " · handled" : ""}
                    </div>
                  </td>
                </tr>
              ))}
            </Table>
          )}
          <Pager count={q.data.items.length} page={pages.length}
            previous={pages.length > 1 ? () => setPages(pages.slice(0, -1)) : undefined}
            next={q.data.next_cursor ? () => setPages([...pages, q.data!.next_cursor]) : undefined}
          />
        </>
      )}
      {opened && <OutboxDetail id={opened} onClose={() => setOpened(null)} />}
      <Dialog title="Inject a simulated failure" open={faultOpen} onClose={() => setFaultOpen(false)}>
        <p>Development/test only. This controls the next mock send.</p>
        <Field label="Channel" id="fault-channel">
          <select id="fault-channel" value={channel} onChange={(e) => setChannel(e.target.value as typeof channel)}>
            {["mail", "calendar", "ats", "hris"].map((v) => (
              <option key={v}>{v}</option>
            ))}
          </select>
        </Field>
        <Field label="Outcome" id="fault-outcome">
          <select id="fault-outcome" value={outcome} onChange={(e) => setOutcome(e.target.value as typeof outcome)}>
            {["failed", "unknown", "retry_after"].map((v) => (
              <option key={v}>{v}</option>
            ))}
          </select>
        </Field>
        <Banner error={fault.error} />
        <div className="acts">
          <button className="btn" onClick={() => setFaultOpen(false)}>
            Cancel
          </button>
          <button
            className="btn btn--primary"
            disabled={fault.isPending}
            onClick={() => fault.mutate({ channel, outcome, count: 1 }, { onSuccess: () => setFaultOpen(false) })}
          >
            Inject failure
          </button>
        </div>
      </Dialog>
    </section>
  );
}
/** A delivery's recipient or sender by name; none recorded is null, so the row draws no empty "from". */
function notificationPerson(directory: Directory, notification: Record<string, unknown>, direction: "to" | "from") {
  const id = notification[`${direction}_person_id`] ?? notification[direction];
  const name = notification[`${direction}_person_name`];
  return typeof id === "string" ? personName(directory, id, typeof name === "string" ? name : null) : id ?? null;
}
function namedNotification(directory: Directory, row: api.OutboxRow) {
  const fields = Object.fromEntries(Object.entries(row.notification).filter(([key]) => !/^(to|from)(?:_person_(?:id|name))?$/.test(key)));
  return { to: notificationPerson(directory, row.notification, "to"), from: notificationPerson(directory, row.notification, "from"), ...fields, subject: deliverySubject(row) };
}
function OutboxDetail({ id, onClose }: { id: string; onClose: () => void }) {
  const directory = useDirectory();
  const q = useRead(["outbox-open", id], () => api.adminOutboxOpen({ id }), { refetchInterval: 5000 });
  const retry = useWrite(api.adminOutboxRetry, [["outbox"], ["outbox-open", id], ["system"]]);
  const handle = useWrite(api.adminOutboxMarkHandled, [["outbox"], ["outbox-open", id], ["system"]]);
  const [handling, setHandling] = useState(false);
  const r = q.data?.row;
  return (
    <Dialog title={r ? `Delivery · ${outboxKind(r.kind)} · ${r.requisition_id ?? r.subject_id ?? "Notification"}` : "Delivery"} open onClose={onClose}>
      <Banner error={q.error} onRetry={() => void q.refetch()} />
      {q.isLoading && <Skeleton />}
      {r && (
        <>
          <div className="hrow"><Pill tone={["failed", "dead"].includes(r.state) ? "bad" : r.state === "sent" ? "ok" : "muted"}>{r.label}</Pill><span>{r.attempts} {r.attempts === 1 ? "attempt" : "attempts"}</span></div>
          {!r.handled_at && ["failed", "dead"].includes(r.state) && (
            <div className="acts">
              <button className="btn" disabled={retry.isPending} onClick={() => retry.mutate({ id })}>
                Retry
              </button>
              <button className="btn" onClick={() => setHandling(true)}>
                Mark as handled
              </button>
            </div>
          )}
          <p className="mono muted">{id}</p>
          {r.last_error && <p className="callout callout--bad">{r.last_error}</p>}
          <p className="callout">{q.data!.simulated_notice}</p>
          <h3 className="sec">Headers and body as sent</h3>
          <RecordFields value={Object.fromEntries(Object.entries(namedNotification(directory, r)).filter(([key]) => !["id", "body_as_sent"].includes(key)))} />
          <section className="delivery-body"><h3 className="sec">Body as sent</h3><div className="prose">{String(r.notification.body_as_sent ?? "Not recorded")}</div></section>
          <KV
            items={[
              ["Next attempt", fmtDate(r.next_attempt_at, true)],
              ["Provider reference", r.provider_ref],
              ["Accepted", fmtDate(r.accepted_at, true)],
              ["Handled by", r.handled_by_id ? personName(directory, r.handled_by_id) : null],
              ["Handled reason", r.handled_reason],
            ]}
          />
          <h3 className="sec">Attempt history</h3>
          <Table label="Attempt history" head={<tr><th scope="col">At</th><th scope="col">Outcome</th><th scope="col">Error</th><th scope="col">Next attempt</th></tr>}>{q.data!.attempt_history.map((attempt, i) => <tr key={i}><td>{fmtDate(attempt.at as string, true)}</td><td>{String(attempt.outcome ?? "Not recorded")}</td><td>{String(attempt.error ?? attempt.last_error ?? "None")}</td><td>{fmtDate(attempt.next_attempt_at as string, true)}</td></tr>)}</Table>
          <Banner error={retry.error} />
          {retry.isSuccess && <Receipt>Retry queued using the original delivery key.</Receipt>}
          {handle.isSuccess && <Receipt>Marked handled; the message is retained.</Receipt>}
        </>
      )}
      <ReasonDialog
        title="Mark delivery handled"
        confirm="Mark handled"
        open={handling}
        onClose={() => setHandling(false)}
        error={handle.error}
        busy={handle.isPending}
        onConfirm={(reason) => handle.mutate({ id, reason }, { onSuccess: () => setHandling(false) })}
      />
    </Dialog>
  );
}
/** BRD ch. 8 "Audit is append-only: old/new, actor, timestamp, reason, related req/candidate, evidence and source." and
 * §13 "audited override authority": the receipt's facts beside its old/new, each only when recorded. */
export function receiptFacts(r: api.AuditRow): [string, string][] {
  const facts: [string, string | null | undefined][] = [
    ["Reason", r.reason],
    // the server's word for the override role (a receipt written before the B12 fold reads "TA Admin (retired)" /
    // "HR Systems Admin (retired)", cockpit ruling 3); roleLabel draws the same words when an older server sends none
    ["Override", r.override_role ? r.override_role_label ?? roleLabel(r.override_role) : null],
    ["Source", r.source],
    ["Evidence", r.evidence_ref],
    ["Related req", r.related_requisition_id],
    ["Related candidate", r.related_candidate_id],
  ];
  return facts.filter((f): f is [string, string] => Boolean(f[1]));
}
const AUDIT_ENTITIES = ["access_grant", "access_request", "application", "archive", "audit_query", "candidate", "clock", "concern", "config_version", "configuration", "debrief_cycle", "feedback", "feedback_export", "file", "intake", "interview", "legal_hold", "offer", "outbox", "outbox_message", "packet", "person", "proposal", "reference_check", "request", "requisition", "route", "scorecard", "session", "stand_in", "summary_sheet", "system", "timer_occurrence", "worker"];
export function AuditTab({ requisitionId, compact = false }: { requisitionId?: string; compact?: boolean } = {}) {
  const directory = useRead(["all-people"], allPeople);
  const people: Directory = new Map((directory.data ?? []).map((p) => [p.person_id, p.name]));
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [draft, setDraft] = useState({ entity_type: "", entity_id: "", tool: "", actor_id: "", on_behalf_of_id: "", outcome: "", receipt_kind: "", related_requisition_id: "", since: "", until: "" });
  const [filters, setFilters] = useState(draft);
  const [pages, setPages] = useState<(number | null)[]>([null]);
  const input = {
    ...Object.fromEntries(Object.entries(filters).map(([k, v]) => [k, v || null])),
    since: date(filters.since),
    until: date(filters.until),
    ...(requisitionId ? { entity_type: "requisition", entity_id: requisitionId } : {}),
    limit: compact ? 5 : 25,
    cursor: pages.at(-1) ?? null,
  };
  const q = useRead(["audit", input], () => api.auditQuery(input));
  const exporter = useWrite(async (_: Record<string, never>) => {
    const result = await api.auditQuery({ ...input, format: "csv" });
    downloadText("audit-page.csv", result.csv ?? "", "text/csv");
  });
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const ledger = useWrite(api.adminVerifyLedger);
  return (
    <section>
      <h2 className="sec">{requisitionId ? compact ? "Recent activity" : "Requisition activity" : "Audit query"}</h2>
      {requisitionId && <p className="muted">Receipts recorded against {requisitionId}. Candidate and interview records keep their own activity.</p>}
      {!compact && <form
        onSubmit={(e) => {
          e.preventDefault();
          setFilters(draft);
          setFiltersOpen(false);
          setPages([null]);
        }}
      >
        <details open={filtersOpen} onToggle={(e) => setFiltersOpen(e.currentTarget.open)}><summary>Filters</summary>
        <Filters
          choices={{ outcome: ["ok", "refused"], receipt_kind: ["admin_override"] }}
          suggestions={{ entity_type: AUDIT_ENTITIES }}
          values={requisitionId ? Object.fromEntries(Object.entries(draft).filter(([key]) => !["entity_type", "entity_id"].includes(key))) : draft}
          dates={["since", "until"]}
          labels={{ actor_id: "Actor", on_behalf_of_id: "On behalf of", receipt_kind: "Override", related_requisition_id: "Requisition", since: "From date", until: "To date" }}
          update={(k, v) => setDraft({ ...draft, [k]: v })}
        />
        </details>
        <div className="acts">
          <button className="btn btn--primary">Query</button>
          <button type="button" className="btn" disabled={exporter.isPending} onClick={() => exporter.mutate({})}>
            Export CSV
          </button>
        </div>
      </form>}
      {!compact && <ActiveFilters display={Object.fromEntries((directory.data ?? []).map(person => [person.person_id, person.name]))} values={filters} labels={{ on_behalf_of_id: "On behalf of", actor_id: "Actor", entity_type: "Entity type", receipt_kind: "Override", related_requisition_id: "Requisition" }} clear={(key) => { const next = { ...filters, [key]: "" }; setFilters(next); setDraft(next); setPages([null]); }} />}
      {!compact && <p className="muted">CSV exports this page of the filtered ledger. Page forward to inspect older receipts.</p>}
      <Banner error={exporter.error} />
      {exporter.isSuccess && <Receipt>Audit page exported.</Receipt>}
      <Banner error={q.error} onRetry={() => void q.refetch()} />
      {(q.data || q.isLoading) && (
        <>
          {!q.isLoading && !q.data?.items.length ? (
            <Empty heading={Object.values(filters).some(Boolean) ? "No matching receipts" : "No receipts yet"} action={Object.values(filters).some(Boolean) ? <button className="btn" onClick={() => { const empty = { entity_type: "", entity_id: "", tool: "", actor_id: "", on_behalf_of_id: "", outcome: "", receipt_kind: "", related_requisition_id: "", since: "", until: "" }; setFilters(empty); setDraft(empty); setPages([null]); }}>Clear filters</button> : undefined}>{Object.values(filters).some(Boolean) ? "No receipts match these filters." : "Receipts appear here when an operation is recorded."}</Empty>
          ) : (
            <Table
              busy={q.isLoading}
              label="Audit receipts"
              head={
                <tr>
                  <th>Receipt / at</th>
                  <th>Actor / on behalf of</th>
                  <th>Tool / entity</th>
                  <th>Outcome</th>
                  <th>Changes / trace</th>
                </tr>
              }
            >
              {(q.data?.items ?? []).map((r) => (
                <Fragment key={r.id}>
                  <tr>
                    <td>
                      #{r.id}
                      <div>{fmtDate(r.at, true)}</div>
                      <div className="muted">Effective {fmtDate(r.effective_at, true)}</div>
                    </td>
                    <td>
                      <Identity name={personName(people, r.actor_id, r.actor_name)} />
                      {r.on_behalf_of_id && <div><Identity relation="for" name={personName(people, r.on_behalf_of_id, r.on_behalf_of_name)} /></div>}
                      {r.view_as_id && <div><Identity relation="viewing" name={personName(people, r.view_as_id, r.view_as_name)} /></div>}
                      {r.acting_for_id && <div><Identity relation="acting for" name={personName(people, r.acting_for_id, r.acting_for_name)} /></div>}
                    </td>
                    <td>
                      {toolWords(r.tool)}
                      <div className="long">
                        {[r.entity_type && recordLabel(r.entity_type), r.entity_type === "person" ? personName(people, r.entity_id) : r.entity_id].filter(Boolean).join(" · ") || MISSING}
                      </div>
                    </td>
                    <td>
                      <Pill tone={r.outcome === "ok" ? "ok" : "bad"}>
                        {AUDIT_OUTCOME[r.outcome] ?? sentence(r.outcome)}
                        {r.refusal_reason ? ` · ${refusalWords(r.refusal_reason)}` : ""}
                      </Pill>
                    </td>
                    <td>
                      <button className="link" aria-expanded={expanded === r.id} onClick={() => setExpanded(expanded === r.id ? null : r.id)}>
                        Changes · #{r.id}
                      </button>
                      <div className="muted long">{r.trace_id ?? "No trace id"}</div>
                    </td>
                  </tr>
                  {expanded === r.id && (
                    <tr>
                      <td colSpan={5}>
                        <section aria-label={`Receipt ${r.id} changes`} className="sheet">
                          <p>{r.summary}</p>
                          <KV items={receiptFacts(r)} wide={["Reason"]} />
                          <DiffTable label={`Receipt ${r.id} diff`} rows={changesRows(r.changes)} />
                          <p className="muted mono">Trace {r.trace_id ?? "Not recorded"} · Receipt #{r.id} · Tool {r.tool}{[["Actor", r.actor_id], ["For", r.on_behalf_of_id], ["Viewing", r.view_as_id], ["Acting for", r.acting_for_id]].filter(([, id]) => id).map(([word, id]) => ` · ${word} ${id}`).join("")}</p>
                        </section>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </Table>
          )}
          {!compact && <Pager count={q.data?.items.length ?? 0} page={pages.length}
            previous={pages.length > 1 ? () => setPages(pages.slice(0, -1)) : undefined}
            next={q.data?.next_cursor ? () => setPages([...pages, q.data!.next_cursor!]) : undefined}
          />}
        </>
      )}
      {!requisitionId && <section className="card">
        <h3 className="sec">Verify ledger</h3>
        <div className="admin-filters">
          <Field label="From receipt" id="ledger-from">
            <input id="ledger-from" type="number" min={1} value={from} onChange={(e) => setFrom(e.target.value)} />
          </Field>
          <Field label="To receipt" id="ledger-to">
            <input id="ledger-to" type="number" min={1} value={to} onChange={(e) => setTo(e.target.value)} />
          </Field>
        </div>
        <button
          className="btn"
          disabled={ledger.isPending}
          onClick={() => ledger.mutate({ from_id: from ? Number(from) : null, to_id: to ? Number(to) : null })}
        >
          Verify ledger
        </button>
        <Banner error={ledger.error} />
        {ledger.data && (
          <Receipt>{ledger.data.intact ? `Chain intact · ${ledger.data.rows_checked} rows` : `First break at #${ledger.data.first_bad_id}`}</Receipt>
        )}
      </section>}
      {compact && requisitionId && <a className="btn" href={`#requisitions/${requisitionId}/activity`}>View all activity</a>}
    </section>
  );
}
function SystemTab() {
  const { me, can } = useSession();
  const directory = useDirectory();
  const sys = useRead(["system"], () => api.adminSystem({}), { refetchInterval: 15_000, staleTime: 0, refetchOnMount: "always" });
  const adapters = useRead(["adapters"], () => api.adminAdapters({}));
  const secrets = useRead(["secrets"], () => api.adminSecrets({}));
  const worker = useRead(["worker"], () => api.adminWorkerStatus({}), { refetchInterval: 15_000 });
  const dead = useRead(["outbox", "dead"], () => api.adminOutboxList({ state: "dead", limit: 100 }), { refetchInterval: 15_000 });
  const inval = [["system"], ["worker"], ["outbox"]] as const;
  const tick = useWrite(api.adminTick, inval);
  const travel = useWrite(api.adminTimeTravel, inval);
  const reset = useWrite(api.adminResetWorld);
  const [resetOpen, setResetOpen] = useState(false);
  const [confirmation, setConfirmation] = useState("");
  const run = useWrite(api.adminTimersRun, inval);
  const handled = useWrite(api.adminTimersMarkHandled, inval);
  const rearm = useWrite(api.adminTimersRearm, inval);
  const [when, setWhen] = useState("");
  const [rid, setRid] = useState("");
  const [action, setAction] = useState<{ kind: "handle" | "rearm"; id: string } | null>(null);
  const s = sys.data,
    w = worker.data;
  const timersOnly = new URLSearchParams(location.hash.split("?")[1]).get("section") === "timers";
  return (
    <section>
      <h2 className="sec">{timersOnly ? "Timers needing attention" : "System"}</h2>
      <Banner error={sys.error} onRetry={() => void sys.refetch()} />
      {sys.isLoading && <Skeleton />}
      {!timersOnly && <>
      <div className="worker-grid">
        <section className="card">
          <h3 className="sec">Adapters</h3>
          <Banner error={adapters.error} onRetry={() => void adapters.refetch()} />
          {adapters.isLoading && <Skeleton />}
          {adapters.data && <>
            <p>{count(adapters.data.items.length, "port")} · {adapters.data.items.filter(a => a.simulated).length} simulated · {adapters.data.items.some(a => a.health.last_error_class) ? count(adapters.data.items.filter(a => a.health.last_error_class).length, "reporting an error", "reporting errors") : "no errors"}</p>
            <Table label="Adapter health" head={<tr><th>Port</th><th>Adapter</th><th>Mode</th><th>Last call</th><th>Last error</th><th>Link</th></tr>}>{adapters.data.items.map(a => <tr key={a.port}><th scope="row">{a.port}</th><td>{a.adapter}</td><td><Pill category={a.simulated} tone={a.simulated ? "muted" : "ok"}>{a.mode}{a.simulated ? " · simulated" : ""}</Pill></td><td>{fmtDate(a.health.last_call_at as string, true)}</td><td>{String(a.health.last_error_class ?? "None")}</td><td>{a.controls.includes("see_outbox") ? <a href="#admin/outbox">See Outbox</a> : <span className="na">N/A</span>}</td></tr>)}</Table>
          </>}
          <p className="muted">Adapter selection comes from environment configuration.</p>
        </section>
        <section className="card">
          <h3 className="sec">Secrets by name</h3>
          <p className="muted">Values are never read by this screen.</p>
          <Banner error={secrets.error} onRetry={() => void secrets.refetch()} />
          {secrets.isLoading && <Skeleton />}
          {secrets.data && (
            <Table
              label="Secret presence"
              head={
                <tr>
                  <th>Name</th>
                  <th>Presence</th>
                  <th>Source</th>
                </tr>
              }
            >
              {secrets.data.items.map((r) => (
                <tr key={r.name}>
                  <td className="long">{r.name}</td>
                  <td>{r.set ? "Set" : "Not set"}</td>
                  <td>{r.source}</td>
                </tr>
              ))}
            </Table>
          )}
        </section>
        <section className="card">
          <h3 className="sec">Worker health</h3>
          <Banner error={worker.error} onRetry={() => void worker.refetch()} />
          {worker.isLoading && <Skeleton />}
          {w && (
            <>
              <KV
                items={[
                  ["Status", <Pill tone={s?.stalled_on ? "bad" : "ok"}>{s?.stalled_on ? `Stalled · ${s.stalled_on.replace(/_/g, " ")}` : "Healthy"}</Pill>],
                  ["Last tick", fmtDate(w.last_tick_at, true)],
                  ["Tick interval", duration(w.tick_interval_s)],
                  ["Oldest unfired due", duration(w.oldest_due_age_s)],
                  ["Worker lock", w.lock_held ? "Held — pass running" : "Free"],
                  ["Dead timers", w.dead_timers],
                  ["Dead outbox rows", w.dead_outbox],
                  ["Oldest queued message", duration(w.outbox_oldest_queued_age_s)],
                ]}
              />
              {(w.oldest_due_age_s ?? 0) > 3 * w.tick_interval_s && (
                <p className="callout callout--bad" role="alert">
                  Oldest unfired due exceeds three tick intervals.
                </p>
              )}
              <h4>Counts by state</h4>
              <RecordFields value={w.counts_by_state} />
              <details>
                <summary>Last ten ticks</summary>
                <RecordFields value={w.recent_ticks} />
              </details>
            </>
          )}
          <p className="muted">Worker health is separate from database readiness.</p>
        </section>
        <section className="card">
          <h3 className="sec">Environment</h3>
          {s && (
            <KV
              items={[
                ["Environment", s.env === "dev" ? "development" : s.env],
                ["App version", s.app_version],
                ["Image digest", s.image_digest],
                ["Seed version", s.seed_version],
                ["Migration head", s.migration_head],
                ["Readiness", s.readiness],
                ["Tools registered", s.tools],
                ["Business time", fmtDate(s.business_now, true)],
              ]}
            />
          )}
          {me && ["dev", "test"].includes(me.env) && (
            <section>
              <h4>Development controls</h4>
              <Field label="Travel to (business time)" id="travel-to">
                <input id="travel-to" type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} />
              </Field>
              <div className="acts">
                <button className="btn" disabled={!when || travel.isPending} onClick={() => travel.mutate({ to: businessLocalToISO(when) })}>
                  Time travel
                </button>
                <button className="btn" disabled={tick.isPending} onClick={() => tick.mutate({})}>
                  Run worker pass
                </button>
                <button className="btn" disabled={travel.isPending} onClick={() => travel.mutate({ advance_hours: 24 })}>
                  +1 day
                </button>
                <button className="btn" disabled={travel.isPending} onClick={() => travel.mutate({ advance_hours: 168 })}>
                  +7 days
                </button>
                <button className="btn" disabled={travel.isPending} onClick={() => travel.mutate({ reset: true })}>
                  Reset clock
                </button>
              </div>
              <Banner error={tick.error ?? travel.error} />
              {tick.data && (
                <Receipt>
                  Worker pass {tick.data.skipped_locked ? "skipped — lock held" : "completed"}.<RecordFields value={tick.data} />
                </Receipt>
              )}
              {travel.data && <Receipt>Business time is now {fmtDate(travel.data.business_now, true)}.</Receipt>}
              {me.env === "dev" && can("admin.reset_world") && (
                <button className="btn btn--danger" onClick={() => setResetOpen(true)}>
                  Reset world
                </button>
              )}
              <Dialog title="Reset synthetic development world" open={resetOpen} onClose={() => setResetOpen(false)}>
                <p>All synthetic records and sessions will be reset. The new audit begins with the reset receipt.</p>
                <Field label="Type RESET WORLD to confirm" id="reset-confirm">
                  <input id="reset-confirm" value={confirmation} onChange={(e) => setConfirmation(e.target.value)} autoComplete="off" />
                </Field>
                <Banner error={reset.error} />
                <div className="acts">
                  <button className="btn" onClick={() => setResetOpen(false)}>
                    Cancel
                  </button>
                  <button
                    className="btn btn--danger"
                    disabled={confirmation !== "RESET WORLD" || reset.isPending}
                    onClick={() =>
                      reset.mutate(
                        { confirmation: "RESET WORLD" },
                        {
                          onSuccess: () => {
                            window.location.hash = "#today";
                            window.location.reload();
                          },
                        },
                      )
                    }
                  >
                    Reset world
                  </button>
                </div>
              </Dialog>
            </section>
          )}
          {me?.env === "dev" && can("workshop.rooms") && <WorkshopRooms />}
        </section>
      </div>
      </>}
      <h3 className="sec">Stale and dead timers</h3>
      <Banner error={run.error} />
      {run.isSuccess && <Receipt>Original occurrence requested. Refreshing its outcome.</Receipt>}
      {handled.isSuccess && <Receipt>Occurrence handled and retained.</Receipt>}
      {w && [...w.stale, ...w.dead].length === 0 && <Empty heading="No timers need attention">No unresolved stale or dead occurrences.</Empty>}
      {w && [...w.stale, ...w.dead].length > 0 && (
        <Table
          label="Unresolved timer occurrences"
          head={
            <tr>
              <th>Occurrence / definition</th>
              <th>Subject</th>
              <th>Scheduled / late</th>
              <th>State / error</th>
              <th>Action</th>
            </tr>
          }
        >
          {[...w.stale, ...w.dead].map((r) => (
            <tr key={r.occurrence_id}>
              <td>
                <div>{r.definition}</div><CopyId value={r.occurrence_id} />
              </td>
              <td>
                {recordLabel(r.subject_type)} · {r.subject_type === "person" ? personName(directory, r.subject_id) : r.subject_id}
              </td>
              <td>
                {fmtDate(r.scheduled_at, true)}
                <div>{duration(r.late_by_seconds)} late</div>
              </td>
              <td>
                <Pill tone={r.state === "dead" ? "bad" : "warn"}>{sentence(r.state)}</Pill> · {r.attempts} {r.attempts === 1 ? "attempt" : "attempts"}<div className="long">{r.detail}</div>
              </td>
              <td>
                <div className="acts">
                  <button className="btn btn--sm" disabled={run.isPending} onClick={() => run.mutate({ occurrence_id: r.occurrence_id })}>
                    Run now
                  </button>
                  <button className="btn btn--sm" onClick={() => setAction({ kind: "handle", id: r.occurrence_id })}>
                    Mark handled
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </Table>
      )}
      <Field label="Requisition to re-arm" id="rearm-rid">
        <input id="rearm-rid" value={rid} onChange={(e) => setRid(e.target.value)} placeholder="R-####" />
      </Field>
      <button className="btn" disabled={!rid.trim()} onClick={() => setAction({ kind: "rearm", id: rid })}>
        Re-arm timers
      </button>
      {rearm.data && (
        <Receipt>
          Re-armed {rearm.data.armed} timers; superseded {rearm.data.superseded} generations.
        </Receipt>
      )}
      <ReasonDialog
        title={action?.kind === "handle" ? "Mark occurrence handled" : "Re-arm requisition timers"}
        confirm={action?.kind === "handle" ? "Mark handled" : "Re-arm"}
        open={!!action}
        onClose={() => setAction(null)}
        error={handled.error ?? rearm.error}
        busy={handled.isPending || rearm.isPending}
        onConfirm={(reason) => {
          const onSuccess = () => setAction(null);
          if (action?.kind === "handle") handled.mutate({ occurrence_id: action.id, reason }, { onSuccess });
          else if (action) rearm.mutate({ requisition_id: action.id, reason }, { onSuccess });
        }}
      />
      <h3 className="sec">Dead letters</h3>
      <Banner error={dead.error} />
      {dead.data &&
        (dead.data.items.length ? (
          <Table
            label="Dead outbox messages"
            head={
              <tr>
                <th>Created</th>
                <th>Kind</th>
                <th>Attempts</th>
                <th>Reason</th>
              </tr>
            }
          >
            {dead.data.items.map((r) => (
              <tr key={r.id}>
                <td>{fmtDate(r.created_at, true)}</td>
                <td>{outboxKind(r.kind)}</td>
                <td>{r.attempts}</td>
                <td className="long">{r.last_error}</td>
              </tr>
            ))}
          </Table>
        ) : (
          <Empty heading="No failed messages to resolve">No dead outbox messages. Failed deliveries remain in the Outbox.</Empty>
        ))}
      <details className="card">
        <summary>Files</summary>
        <p className="muted">Quarantined, rejected and abandoned files are retained with their reasons.</p>
        {can("admin.files_list") ? (
          <FileInventory />
        ) : (
          <p className="callout callout--warn" role="status">
            The file inventory is not available yet. This is not an empty inventory. Available attachments can be read from their owning record.
          </p>
        )}
      </details>
    </section>
  );
}

function FileInventory() {
  const [status, setStatus] = useState<NonNullable<api.AdminFilesListInput["status"]> | "">("");
  const [pages, setPages] = useState<(string | null)[]>([null]);
  const q = useRead(["admin-files", status, pages.at(-1)], () => api.adminFilesList({ status: status || null, cursor: pages.at(-1) ?? null, limit: 25 }));
  return (
    <section aria-label="Retained file inventory">
      <Field label="File status" id="inventory-status">
        <select
          id="inventory-status"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as typeof status);
            setPages([null]);
          }}
        >
          <option value="">Uploads awaiting attachment or unavailable</option>
          {["quarantined", "scanned_clean", "rejected", "abandoned"].map((s) => (
            <option key={s} value={s}>{roleLabel(s)}</option>
          ))}
        </select>
      </Field>
      <Banner error={q.error} onRetry={() => void q.refetch()} />
      {q.isLoading && <Skeleton />}
      {q.data && (
        <>
          <p className="muted">Maximum upload: {q.data.max_upload_bytes / 1024 / 1024} MiB. Files are retained; there is no erasure control.</p>
          {q.data.items.length ? <Table label="Retained uploads" head={<tr><th>File / version</th><th>Kind / owner</th><th>Status / reason</th><th>Size / SHA-256</th></tr>}>{q.data.items.map((file) => <tr key={String(file.file_id)}><td><div>{roleLabel(String(file.kind))} · v{String(file.version ?? "N/A")}</div><CopyId value={String(file.file_id)} /></td><td>{roleLabel(String(file.kind))}<div>{String(file.owner_type).replace(/_/g, " ")} · {String(file.owner_id)}</div></td><td><Pill tone={file.status === "rejected" ? "bad" : "info"}>{roleLabel(String(file.status))}</Pill>{file.reject_reason ? <div>{String(file.reject_reason).replace(/_/g, " ")}</div> : null}</td><td>{file.size_bytes == null ? "No measured size — legacy reference" : `${String(file.size_bytes)} B`}{file.sha256 ? <div className="mono" title={String(file.sha256)}>SHA-256 (first 10): {String(file.sha256).slice(0, 10)}…</div> : <div>No hash — legacy reference</div>}</td></tr>)}</Table> : <Empty heading={status ? "No matching uploads" : "No retained uploads yet"} action={status ? <button className="btn" onClick={() => { setStatus(""); setPages([null]); }}>Clear status filter</button> : undefined}>{status ? "No retained uploads match this status." : "Uploads appear here when a file is retained."}</Empty>}
          <Pager count={q.data.items.length} page={pages.length}
            previous={pages.length > 1 ? () => setPages(pages.slice(0, -1)) : undefined}
            next={q.data.next_cursor ? () => setPages([...pages, q.data!.next_cursor]) : undefined}
          />
        </>
      )}
    </section>
  );
}

/**
 * The development Workshop's rooms (backend/app/tools/workshop.py; sessions 26 Oct to 19 Nov 2026): one Reset room
 * button per live room, confirmed by typing reset. A reset withdraws the room's candidate and builds the room again as a
 * new requisition through the application's own tools; nothing is deleted. A room's Admin sees its own room. Development
 * only, like Reset world above.
 */
function WorkshopRooms() {
  const rooms = useRead(["workshop-rooms"], () => api.workshopRooms({}));
  const reset = useWrite(api.workshopResetRoom, [["workshop-rooms"], ["system"]]);
  const [target, setTarget] = useState<api.WorkshopRoomsOutput["rooms"][number] | null>(null);
  const [confirmation, setConfirmation] = useState("");
  const close = () => { setTarget(null); setConfirmation(""); };
  const live = (rooms.data?.rooms ?? []).filter((r) => r.live);
  const name = (r: api.WorkshopRoomsOutput["rooms"][number]) => (r.room ? `Room ${r.room}` : "Workshop") + ` · ${r.requisition_id}`;
  return (
    <section>
      <h4>Workshop rooms</h4>
      <Banner error={rooms.error} />
      {rooms.data && live.length === 0 && <p>No live workshop room.</p>}
      {live.map((r) => (
        <div className="acts" key={r.requisition_id}>
          <span>{name(r)}</span>
          <button className="btn btn--danger" onClick={() => setTarget(r)} disabled={reset.isPending}>
            Reset room
          </button>
        </div>
      ))}
      {reset.isPending && <p role="status">Resetting the room…</p>}
      {reset.data && (
        <Receipt>
          {reset.data.failed_step
            ? `The reset stopped at ${reset.data.failed_step}: ${reset.data.failure ?? ""}`
            : `${reset.data.room ? `Room ${reset.data.room}` : "Workshop"} reset. New requisition ${reset.data.new_requisition_id ?? MISSING}.`}
        </Receipt>
      )}
      <Dialog title={target ? `Reset ${name(target)}` : "Reset room"} open={target !== null} onClose={close}>
        <p>The room starts over with a fresh candidate and blank interviewer scorecards. Nothing is deleted.</p>
        <Field label="Type reset to confirm" id="reset-room-confirm">
          <input id="reset-room-confirm" value={confirmation} onChange={(e) => setConfirmation(e.target.value)} autoComplete="off" />
        </Field>
        <Banner error={reset.error} />
        <div className="acts">
          <button className="btn" onClick={close}>
            Cancel
          </button>
          <button
            className="btn btn--danger"
            disabled={confirmation.trim().toLowerCase() !== "reset" || reset.isPending || !target}
            onClick={() => target && reset.mutate({ requisition_id: target.requisition_id, confirmation: "reset" }, { onSuccess: close })}
          >
            {reset.isPending ? "Resetting…" : "Reset room"}
          </button>
        </div>
      </Dialog>
    </section>
  );
}
